import { DataType } from '@revolist/react-datagrid';

import {
  SelectOption as GridSelectOption,
  toSelectOption,
} from 'src/components/streametric/revoGridTable';
import { tagLabelOptions } from 'src/config/tagLabels';
import { ALLOWED_INPUTS } from 'src/hooks/featureHooks/useTagTableState/types/allowedInputs';
import { CONTROLLABLE } from 'src/hooks/featureHooks/useTagTableState/types/controllable';
import {
  DATA_TYPE_OF_VALUES,
  DataTypeOfValuesValue,
} from 'src/hooks/featureHooks/useTagTableState/types/dataTypeOfValues';
import { TAG_TYPE } from 'src/hooks/featureHooks/useTagTableState/types/tagType';
import { Controller } from 'src/types/controllers/controller';
import { LabelingTypeEnum } from 'src/types/tags';

import { BOOL_INTEGER_UNITS } from './data/boolIntegerUnits';
import {
  CategoryOption,
  MODE_CATEGORY_OPTIONS_BASIC,
  MODE_CATEGORY_OPTIONS_EXTENDED,
} from './data/modeCategoryOptions';
import { RAW_UNITS } from './data/rawUnits';
import { STATE_CATEGORY_OPTIONS } from './data/stateCategoryOptions';
import { EditTagFormValues } from './types/formValues';
import { MODE_CATEGORY } from './types/modeCategory';

type SelectOption = { label: string; value: string };

// =============================================================================
// Visibility — is a field/section shown at all?
// =============================================================================

// Tag types with no "category" concept for predefined values.
const HIDDEN_CATEGORY_TAG_TYPES = new Set<string>([TAG_TYPE.SETPOINT, TAG_TYPE.OUTPUT]);

/** The Advanced section is meaningless until a Tag Type is chosen — every
 * option list under it is keyed on Tag Type. */
export const isVisibleAdvancedSection = (form: Pick<EditTagFormValues, 'dataType'>): boolean =>
  Boolean(form.dataType);

export const isVisibleMinMaxValue = (form: Pick<EditTagFormValues, 'allowedInputs'>): boolean =>
  form.allowedInputs === ALLOWED_INPUTS.RANGE;

/** Same condition as Undefined Label's own visibility — one function backs both. */
export const isVisiblePredefinedValues = (
  form: Pick<EditTagFormValues, 'allowedInputs'>
): boolean => !isVisibleMinMaxValue(form);

export const isVisibleMaxDecimal = (form: Pick<EditTagFormValues, 'dataTypeOfValues'>): boolean =>
  form.dataTypeOfValues === DATA_TYPE_OF_VALUES.FLOAT;

/** Tag types whose readings are shown as a number, so rounding them for display means something. */
const NUMERIC_DISPLAY_TAG_TYPES = new Set<string>([
  TAG_TYPE.SETPOINT,
  TAG_TYPE.SENSOR_DATA,
  TAG_TYPE.OUTPUT,
]);

/** Rounding a reading for display only applies to a numeric one — Boolean values render as their label. */
export const isVisibleMaxDisplayDecimal = (
  form: Pick<EditTagFormValues, 'dataType' | 'dataTypeOfValues'>
): boolean =>
  NUMERIC_DISPLAY_TAG_TYPES.has(form.dataType.toLowerCase()) &&
  (form.dataTypeOfValues === DATA_TYPE_OF_VALUES.FLOAT ||
    form.dataTypeOfValues === DATA_TYPE_OF_VALUES.INTEGER);

export const isVisibleCategoryColumn = (form: Pick<EditTagFormValues, 'dataType'>): boolean =>
  !HIDDEN_CATEGORY_TAG_TYPES.has(form.dataType.toLowerCase());

export const isVisibleRelevantMode = (
  form: Pick<EditTagFormValues, 'controllable' | 'dataType'>
): boolean =>
  form.controllable === CONTROLLABLE.YES && form.dataType.toLowerCase() !== TAG_TYPE.MODE;

// =============================================================================
// Disabled / locked state
// =============================================================================

export const isDisabledDataType = (
  form: Pick<EditTagFormValues, 'allowedInputs' | 'dataType'>
): boolean => getDataTypeOptions(form).length === 0;

export const isDisabledRawUnit = (form: Pick<EditTagFormValues, 'dataType'>): boolean =>
  getRawUnitOptions(form).length === 0;

export const isDisabledDisplayAs = (form: Pick<EditTagFormValues, 'allowedInputs'>): boolean =>
  getDisplayAsOptions(form).length <= 1;

export const isValuesTableLocked = (form: Pick<EditTagFormValues, 'dataTypeOfValues'>): boolean =>
  form.dataTypeOfValues === DATA_TYPE_OF_VALUES.BOOLEAN;

/** Label reads as "Value" normally, or "False Value"/"True Value" once the table is locked to Boolean's fixed 2 rows. */
export const getValueRowLabel = (
  form: Pick<EditTagFormValues, 'dataTypeOfValues'>,
  rowIndex: number
): string => {
  if (!isValuesTableLocked(form)) return 'Value';
  return rowIndex === 0 ? 'False Value' : 'True Value';
};

// =============================================================================
// Options — which choices a field can show
// =============================================================================

const BOOL_INTEGER_TAG_TYPES = new Set<string>([TAG_TYPE.MODE, TAG_TYPE.STATE, TAG_TYPE.ALARM]);

// Shared with `tagTableColumns.ts`'s RevoGrid column config — signatures must not change.
export const resolveRawUnitOptions = (model: DataType): readonly GridSelectOption[] => {
  const tagType = ((model?.dataType as string) ?? '').toLowerCase();
  if (!tagType) return [];
  return BOOL_INTEGER_TAG_TYPES.has(tagType) ? BOOL_INTEGER_UNITS : RAW_UNITS;
};

const INTEGER_OPTION: GridSelectOption = { label: 'Integer', value: DATA_TYPE_OF_VALUES.INTEGER };
const BOOLEAN_OPTION: GridSelectOption = { label: 'Boolean', value: DATA_TYPE_OF_VALUES.BOOLEAN };
const FLOAT_OPTION: GridSelectOption = { label: 'Float', value: DATA_TYPE_OF_VALUES.FLOAT };

export const resolveDataTypeOptions = (model: DataType): readonly GridSelectOption[] => {
  const tagType = ((model?.dataType as string) ?? '').toLowerCase();
  if (tagType === '') return [];

  const allowedInputs = model?.allowedInputs as string;
  const isBoolIntegerTagType = BOOL_INTEGER_TAG_TYPES.has(tagType);

  if (allowedInputs === ALLOWED_INPUTS.RANGE) {
    return isBoolIntegerTagType ? [INTEGER_OPTION] : [INTEGER_OPTION, FLOAT_OPTION];
  }
  return isBoolIntegerTagType
    ? [INTEGER_OPTION, BOOLEAN_OPTION]
    : [INTEGER_OPTION, BOOLEAN_OPTION, FLOAT_OPTION];
};

export const getDataTypeOptions = (
  form: Pick<EditTagFormValues, 'allowedInputs' | 'dataType'>
): SelectOption[] =>
  resolveDataTypeOptions({ dataType: form.dataType, allowedInputs: form.allowedInputs }).map(
    toSelectOption
  );

export const getRawUnitOptions = (form: Pick<EditTagFormValues, 'dataType'>): SelectOption[] =>
  resolveRawUnitOptions({ dataType: form.dataType }).map(toSelectOption);

const ROW_COUNT_BY_DATA_TYPE: Record<DataTypeOfValuesValue, number> = {
  [DATA_TYPE_OF_VALUES.BOOLEAN]: 2,
  [DATA_TYPE_OF_VALUES.INTEGER]: 3,
  [DATA_TYPE_OF_VALUES.FLOAT]: 1,
};

export const getRowCountForDataType = (dataTypeOfValues: DataTypeOfValuesValue | ''): number =>
  dataTypeOfValues ? ROW_COUNT_BY_DATA_TYPE[dataTypeOfValues] : 0;

// Whole numbers only for Integer/Boolean; up to Max Decimal for Float.
export const getDecimalPrecisionLimit = (
  form: Pick<EditTagFormValues, 'dataTypeOfValues' | 'maxDecimal'>
): number => (isVisibleMaxDecimal(form) ? Number(form.maxDecimal) || 0 : 0);

/** A Range input has no discrete value to label, so "Display as" locks to Value only. */
export const getDisplayAsOptions = (
  form: Pick<EditTagFormValues, 'allowedInputs'>
): SelectOption[] =>
  form.allowedInputs === ALLOWED_INPUTS.RANGE
    ? tagLabelOptions.filter((option) => option.value === LabelingTypeEnum.VALUE)
    : tagLabelOptions;

/** Which row (if any) currently holds `STORM_MODE` */
const getStormModeLockedRowIndex = (form: Pick<EditTagFormValues, 'values'>): number | null => {
  const index = form.values.findIndex((row) => row.category === MODE_CATEGORY.STORM_MODE);
  return index === -1 ? null : index;
};

export const getCategoryOptions = (
  form: Pick<EditTagFormValues, 'dataType' | 'controllerId' | 'values'>,
  controllers: Controller[],
  rowIndex: number
): CategoryOption[] => {
  const isMode = form.dataType.toLowerCase() === TAG_TYPE.MODE;
  const basic = isMode ? MODE_CATEGORY_OPTIONS_BASIC : STATE_CATEGORY_OPTIONS;
  const extended = isMode ? MODE_CATEGORY_OPTIONS_EXTENDED : STATE_CATEGORY_OPTIONS;
  const controllerType = controllers.find(
    (controller) => controller.id === form.controllerId
  )?.controllerType;
  if (controllerType !== 'storm_mode') return basic;
  const stormModeLockedRowIndex = getStormModeLockedRowIndex(form);
  const lockedByAnotherRow =
    stormModeLockedRowIndex !== null && stormModeLockedRowIndex !== rowIndex;
  return lockedByAnotherRow ? basic : extended;
};
